import { useEffect, useState } from "react";
import { UserPlus, Flame, Video, Copy, Compass, HeartHandshake, Minus, Plus, Unlink, Film, Lock, Unlock, Trash2, Pencil, Mail } from "lucide-react";
import { portalApi, apiError } from "@/lib/portalApi";
import { MOVEMENTS } from "./Journey";
import ContentEditor from "./ContentEditor";
import { toast } from "sonner";

const input = "w-full bg-white border border-[#D8CEBE] rounded-md px-4 py-2.5 text-[#2A2825] font-body outline-none focus:border-[#A9724E]";
const label = "block text-xs tracking-overline uppercase text-[#8a7a6a] mb-1.5";

const movementForWeek = (w) => MOVEMENTS.find((m) => {
  const [s, e] = m.weeks.replace("Weeks ", "").split("–").map((x) => parseInt(x, 10));
  return w >= s && w <= e;
});

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section data-testid={testid} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <Icon className="w-5 h-5 text-[#A9724E]" strokeWidth={1.5} />
        <h2 className="font-serif-display text-2xl">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function ManagePortal() {
  const [members, setMembers] = useState([]);
  const [m, setM] = useState({ name: "", email: "", password: "", track: "vessels", send_welcome: true });
  const [lastCreated, setLastCreated] = useState(null);
  const [ch, setCh] = useState({ title: "", description: "", points: 10, scheduled_for: "" });
  const [call, setCall] = useState({ title: "", description: "", zoom_url: "", starts_at: "" });
  const [week, setWeek] = useState(0);
  const [pair, setPair] = useState({ a: "", b: "" });
  const [limits, setLimits] = useState({ max_video_mb: 50, max_image_mb: 10 });
  const [editMem, setEditMem] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const loadMembers = () => portalApi.get("/portal/members").then((r) => setMembers(r.data)).catch(() => {});
  const loadWeek = () => portalApi.get("/portal/cohort").then((r) => setWeek(r.data.current_week || 0)).catch(() => {});
  const loadLimits = () => portalApi.get("/portal/settings/media").then((r) => setLimits(r.data)).catch(() => {});
  useEffect(() => { loadMembers(); loadWeek(); loadLimits(); }, []);

  const saveLimits = async () => {
    const v = Math.max(1, parseInt(limits.max_video_mb, 10) || 50);
    const im = Math.max(1, parseInt(limits.max_image_mb, 10) || 10);
    try { const { data } = await portalApi.put("/portal/settings/media", { max_video_mb: v, max_image_mb: im }); setLimits(data); toast.success("Media limits updated."); }
    catch (err) { toast.error(apiError(err)); }
  };

  const saveWeek = async (w) => {
    const next = Math.max(0, Math.min(24, w));
    setWeek(next);
    try { await portalApi.put("/portal/cohort/week", { current_week: next }); toast.success(next === 0 ? "Cohort set to not-yet-begun." : `Cohort is now on Week ${next}.`); }
    catch (err) { toast.error(apiError(err)); }
  };

  const pairMembers = async (e) => {
    e.preventDefault();
    if (!pair.a || !pair.b || pair.a === pair.b) return toast.error("Pick two different members.");
    try { await portalApi.post("/portal/pair", { member_a: pair.a, member_b: pair.b }); toast.success("Formation Duo paired."); setPair({ a: "", b: "" }); loadMembers(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const unpair = async (id) => {
    try { await portalApi.post("/portal/unpair", { member_id: id }); toast.success("Unpaired."); loadMembers(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const nameById = (id) => members.find((x) => x.id === id)?.name || "—";
  const seenPairs = new Set();

  const toggleLock = async (mem) => {
    try { await portalApi.put(`/portal/members/${mem.id}/lock`, { locked: !mem.locked }); toast.success(mem.locked ? "Account restored." : "Account paused."); loadMembers(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const removeMember = async (mem) => {
    if (!window.confirm(`Delete ${mem.name}? This permanently removes their account and everything they've shared. This can't be undone.`)) return;
    try { await portalApi.delete(`/portal/members/${mem.id}`); toast.success(`${mem.name.split(" ")[0]} removed.`); loadMembers(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const saveEditMem = async () => {
    setSavingEdit(true);
    try {
      await portalApi.put(`/portal/members/${editMem.id}`, { name: editMem.name, email: editMem.email, track: editMem.track });
      toast.success("Member updated."); setEditMem(null); loadMembers();
    } catch (err) { toast.error(apiError(err)); } finally { setSavingEdit(false); }
  };

  const setMemberPassword = async () => {
    if (!editMem.newPassword || editMem.newPassword.length < 6) return toast.error("Password must be at least 6 characters.");
    try {
      const { data } = await portalApi.put(`/portal/members/${editMem.id}/password`, { password: editMem.newPassword, notify: !!editMem.notify, origin: window.location.origin });
      toast.success(data.emailed ? "Password updated & emailed to them." : "Password updated.");
      setEditMem({ ...editMem, newPassword: "", notify: false });
    } catch (err) { toast.error(apiError(err)); }
  };

  const sendResetLink = async () => {
    try {
      const { data } = await portalApi.post(`/portal/members/${editMem.id}/send-reset`, { origin: window.location.origin });
      toast.success(data.emailed ? "Reset link emailed to them." : "Reset link created (email not configured).");
    } catch (err) { toast.error(apiError(err)); }
  };

  const addMember = async (e) => {
    e.preventDefault();
    try {
      await portalApi.post("/portal/members", { ...m, origin: window.location.origin });
      setLastCreated({ name: m.name, email: m.email, password: m.password, sent: !!m.send_welcome });
      toast.success(m.send_welcome ? `${m.name} added — welcome email sent.` : `${m.name} added. Share their login details.`);
      setM({ name: "", email: "", password: "", track: "vessels", send_welcome: true });
      loadMembers();
    } catch (err) { toast.error(apiError(err)); }
  };

  const copyLogin = (cred) => {
    const url = `${window.location.origin}/portal/login`;
    const text = `Outpoured Portal login\nEmail: ${cred.email}\nTemporary password: ${cred.password}\nSign in: ${url}`;
    navigator.clipboard?.writeText(text);
    toast.success("Login details copied.");
  };

  const resendWelcome = async (mem) => {
    try {
      const { data } = await portalApi.post(`/portal/members/${mem.id}/welcome`, { origin: window.location.origin });
      toast.success(data.emailed ? `Welcome email resent to ${mem.name.split(" ")[0]}.` : "Couldn't send — email not deliverable.");
    } catch (err) { toast.error(apiError(err)); }
  };

  const addChallenge = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...ch, points: Number(ch.points), scheduled_for: ch.scheduled_for || null };
      await portalApi.post("/portal/challenges", payload);
      toast.success("Challenge posted.");
      setCh({ title: "", description: "", points: 10, scheduled_for: "" });
    } catch (err) { toast.error(apiError(err)); }
  };

  const addCall = async (e) => {
    e.preventDefault();
    try {
      if (!call.starts_at) return toast.error("Pick a date & time.");
      await portalApi.post("/portal/calls", { ...call, starts_at: new Date(call.starts_at).toISOString() });
      toast.success("Call scheduled.");
      setCall({ title: "", description: "", zoom_url: "", starts_at: "" });
    } catch (err) { toast.error(apiError(err)); }
  };

  const copyCreds = (mem) => { navigator.clipboard?.writeText(mem.email); toast.success("Email copied"); };

  return (
    <div data-testid="portal-manage">
      <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">Admin</p>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-10">Manage the cohort</h1>

      <div className="grid lg:grid-cols-2 gap-6">
        <Section icon={UserPlus} title="Add a member" testid="manage-add-member">
          <form onSubmit={addMember} className="space-y-4">
            <div><label className={label}>Full name</label><input data-testid="member-name" required className={input} value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} /></div>
            <div><label className={label}>Email</label><input data-testid="member-email" type="email" required className={input} value={m.email} onChange={(e) => setM({ ...m, email: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={label}>Temporary password</label><input data-testid="member-password" required minLength={6} className={input} value={m.password} onChange={(e) => setM({ ...m, password: e.target.value })} /></div>
              <div><label className={label}>Track</label>
                <select data-testid="member-track" className={input} value={m.track} onChange={(e) => setM({ ...m, track: e.target.value })}>
                  <option value="vessels">VESSELS</option>
                  <option value="vows_vessels">Vows &amp; Vessels</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2.5 text-sm text-[#5E4B3C] font-body cursor-pointer">
              <input data-testid="member-send-welcome" type="checkbox" checked={m.send_welcome} onChange={(e) => setM({ ...m, send_welcome: e.target.checked })} />
              Email them a welcome with their login details
            </label>
            <button data-testid="member-submit" className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm tracking-wide transition-colors">Create member</button>
          </form>
          {lastCreated && (
            <div data-testid="last-created-creds" className="mt-5 rounded-xl border border-[#A9724E]/30 bg-[#EAE3D9]/60 p-5">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-xs tracking-overline uppercase text-[#8a7a6a]">{lastCreated.name} · login details</p>
                <button onClick={() => setLastCreated(null)} className="text-[#8a7a6a] hover:text-[#A9724E] text-lg leading-none">×</button>
              </div>
              <p className="text-sm font-body text-[#2A2825]"><span className="text-[#8a7a6a]">Email:</span> {lastCreated.email}</p>
              <p className="text-sm font-body text-[#2A2825]"><span className="text-[#8a7a6a]">Temp password:</span> {lastCreated.password}</p>
              <p className="text-xs font-body text-[#8a7a6a] mt-1">{lastCreated.sent ? "A welcome email was sent to them." : "No email sent — share these yourself."}</p>
              <button data-testid="copy-login-details" onClick={() => copyLogin(lastCreated)} className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-4 py-2 text-xs tracking-wide transition-colors">
                <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />Copy login details
              </button>
            </div>
          )}
          <div className="mt-6 border-t border-[#E0D8C9] pt-5">
            <p className="text-xs tracking-overline uppercase text-[#8a7a6a] mb-3">{members.length} members</p>
            <div className="space-y-2 max-h-52 overflow-y-auto">
              {members.map((mem) => (
                <div key={mem.id} data-testid={`manage-member-${mem.id}`} className="flex items-center justify-between gap-2 text-sm font-body">
                  <span className="text-[#2A2825] min-w-0 truncate">{mem.name} <span className="text-[#8a7a6a]">· {mem.email}</span>{mem.locked && <span className="ml-2 text-[10px] tracking-overline uppercase text-[#b45c5c]">paused</span>}</span>
                  <span className="flex items-center gap-3 shrink-0">
                    <button onClick={() => copyCreds(mem)} title="Copy email" className="text-[#A9724E]"><Copy className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                    <button data-testid={`resend-welcome-${mem.id}`} onClick={() => resendWelcome(mem)} title="Resend welcome email" className="text-[#8a7a6a] hover:text-[#A9724E]"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                    <button data-testid={`edit-member-${mem.id}`} onClick={() => setEditMem({ id: mem.id, name: mem.name, email: mem.email, track: mem.track || "vessels" })} title="Edit member" className="text-[#8a7a6a] hover:text-[#A9724E]"><Pencil className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                    <button data-testid={`lock-member-${mem.id}`} onClick={() => toggleLock(mem)} title={mem.locked ? "Restore access" : "Pause access"} className="text-[#8a7a6a] hover:text-[#A9724E]">
                      {mem.locked ? <Unlock className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    </button>
                    <button data-testid={`delete-member-${mem.id}`} onClick={() => removeMember(mem)} title="Delete member" className="text-[#b45c5c] hover:text-[#933]"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section icon={Flame} title="Post a challenge" testid="manage-add-challenge">
          <form onSubmit={addChallenge} className="space-y-4">
            <div><label className={label}>Title</label><input data-testid="challenge-title" required className={input} value={ch.title} onChange={(e) => setCh({ ...ch, title: e.target.value })} /></div>
            <div><label className={label}>Description</label><textarea data-testid="challenge-desc" rows={3} className={input} value={ch.description} onChange={(e) => setCh({ ...ch, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={label}>Drops</label><input data-testid="challenge-points" type="number" min={1} className={input} value={ch.points} onChange={(e) => setCh({ ...ch, points: e.target.value })} /></div>
              <div><label className={label}>Date (optional)</label><input data-testid="challenge-date" type="date" className={input} value={ch.scheduled_for} onChange={(e) => setCh({ ...ch, scheduled_for: e.target.value })} /></div>
            </div>
            <button data-testid="challenge-submit" className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm tracking-wide transition-colors">Post challenge</button>
          </form>
        </Section>

        <Section icon={Video} title="Schedule a Zoom call" testid="manage-add-call">
          <form onSubmit={addCall} className="space-y-4">
            <div><label className={label}>Title</label><input data-testid="call-title" required className={input} value={call.title} onChange={(e) => setCall({ ...call, title: e.target.value })} /></div>
            <div><label className={label}>Zoom link</label><input data-testid="call-url" type="url" required placeholder="https://zoom.us/j/…" className={input} value={call.zoom_url} onChange={(e) => setCall({ ...call, zoom_url: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={label}>Date &amp; time</label><input data-testid="call-time" type="datetime-local" required className={input} value={call.starts_at} onChange={(e) => setCall({ ...call, starts_at: e.target.value })} /></div>
              <div><label className={label}>Note (optional)</label><input data-testid="call-desc" className={input} value={call.description} onChange={(e) => setCall({ ...call, description: e.target.value })} /></div>
            </div>
            <button data-testid="call-submit" className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm tracking-wide transition-colors">Schedule call</button>
          </form>
        </Section>

        <Section icon={Compass} title="Advance the week" testid="manage-week">
          <p className="text-sm text-[#5E4B3C] font-body mb-5">Set where the whole cohort is. This lights up the current Movement on everyone's Journey, Formation, and Today.</p>
          <div className="flex items-center gap-4">
            <button data-testid="week-minus" onClick={() => saveWeek(week - 1)} className="rounded-full bg-[#EAE3D9] hover:bg-[#e0d8c9] text-[#A9724E] p-3 transition-colors"><Minus className="w-4 h-4" strokeWidth={2} /></button>
            <div className="text-center min-w-[8rem]">
              <p data-testid="week-value" className="font-serif-display text-4xl text-[#2A2825]">{week === 0 ? "—" : `Week ${week}`}</p>
              <p className="text-xs text-[#A9724E] font-body mt-1">{week === 0 ? "Not yet begun" : (movementForWeek(week)?.name || "")}</p>
            </div>
            <button data-testid="week-plus" onClick={() => saveWeek(week + 1)} className="rounded-full bg-[#EAE3D9] hover:bg-[#e0d8c9] text-[#A9724E] p-3 transition-colors"><Plus className="w-4 h-4" strokeWidth={2} /></button>
          </div>
        </Section>

        <Section icon={HeartHandshake} title="Formation Duos" testid="manage-duos">
          <form onSubmit={pairMembers} className="grid grid-cols-2 gap-4 items-end">
            <div><label className={label}>Vessel one</label>
              <select data-testid="pair-a" className={input} value={pair.a} onChange={(e) => setPair({ ...pair, a: e.target.value })}>
                <option value="">Select…</option>
                {members.map((mem) => <option key={mem.id} value={mem.id}>{mem.name}</option>)}
              </select>
            </div>
            <div><label className={label}>Vessel two</label>
              <select data-testid="pair-b" className={input} value={pair.b} onChange={(e) => setPair({ ...pair, b: e.target.value })}>
                <option value="">Select…</option>
                {members.map((mem) => <option key={mem.id} value={mem.id}>{mem.name}</option>)}
              </select>
            </div>
            <button data-testid="pair-submit" className="col-span-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm tracking-wide transition-colors">Pair as a Duo</button>
          </form>
          <div className="mt-6 border-t border-[#E0D8C9] pt-5 space-y-2">
            <p className="text-xs tracking-overline uppercase text-[#8a7a6a] mb-3">Current duos</p>
            {members.filter((mem) => mem.partner_id).length === 0 && <p className="text-sm text-[#8a7a6a] font-body">No duos yet.</p>}
            {members.filter((mem) => mem.partner_id).map((mem) => {
              const key = [mem.id, mem.partner_id].sort().join("_");
              if (seenPairs.has(key)) return null;
              seenPairs.add(key);
              return (
                <div key={key} className="flex items-center justify-between text-sm font-body">
                  <span className="text-[#2A2825]">{mem.name} <span className="text-[#A9724E]">&amp;</span> {nameById(mem.partner_id)}</span>
                  <button onClick={() => unpair(mem.id)} className="text-[#b45c5c] hover:text-[#933]" title="Unpair"><Unlink className="w-4 h-4" strokeWidth={1.5} /></button>
                </div>
              );
            })}
          </div>
        </Section>

        <Section icon={Film} title="Media upload limits" testid="manage-media">
          <p className="text-sm text-[#5E4B3C] font-body mb-5">Maximum file size members can upload for photos and videos in Community, reflections, and Duos.</p>
          <div className="grid grid-cols-2 gap-4 items-end">
            <div><label className={label}>Max video (MB)</label><input data-testid="limit-video" type="number" min={1} className={input} value={limits.max_video_mb} onChange={(e) => setLimits({ ...limits, max_video_mb: e.target.value })} /></div>
            <div><label className={label}>Max photo (MB)</label><input data-testid="limit-image" type="number" min={1} className={input} value={limits.max_image_mb} onChange={(e) => setLimits({ ...limits, max_image_mb: e.target.value })} /></div>
          </div>
          <button data-testid="limit-save" onClick={saveLimits} className="mt-4 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm tracking-wide transition-colors">Save limits</button>
        </Section>
      </div>

      <ContentEditor />

      {editMem && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditMem(null)}>
          <div data-testid="edit-member-modal" className="bg-[#F4EFE6] text-[#2A2825] rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-1">Edit member</p>
            <h3 className="font-serif-display text-2xl mb-5">Fix their details</h3>
            <div className="space-y-4">
              <div><label className={label}>Name</label><input data-testid="edit-member-name" className={input} value={editMem.name} onChange={(e) => setEditMem({ ...editMem, name: e.target.value })} /></div>
              <div><label className={label}>Email</label><input data-testid="edit-member-email" type="email" className={input} value={editMem.email} onChange={(e) => setEditMem({ ...editMem, email: e.target.value })} /></div>
              <div><label className={label}>Home</label>
                <select data-testid="edit-member-track" className={input} value={editMem.track} onChange={(e) => setEditMem({ ...editMem, track: e.target.value })}>
                  <option value="vessels">VESSELS</option>
                  <option value="vows_vessels">Vows &amp; Vessels</option>
                </select>
              </div>
            </div>
            <div className="mt-6 border-t border-[#E0D8C9] pt-5">
              <p className="text-xs tracking-overline uppercase text-[#8a7a6a] mb-3">Password</p>
              <label className={label}>Set a new password</label>
              <input data-testid="edit-member-password" type="text" minLength={6} placeholder="At least 6 characters" className={input}
                value={editMem.newPassword || ""} onChange={(e) => setEditMem({ ...editMem, newPassword: e.target.value })} />
              <label className="flex items-center gap-2.5 text-sm text-[#5E4B3C] font-body cursor-pointer mt-3">
                <input data-testid="edit-member-notify" type="checkbox" checked={!!editMem.notify} onChange={(e) => setEditMem({ ...editMem, notify: e.target.checked })} />
                Email the new login details to them
              </label>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <button data-testid="edit-member-set-password" onClick={setMemberPassword}
                  className="rounded-full bg-[#2A2825] hover:bg-[#413d38] text-[#F4EFE6] px-5 py-2 text-xs tracking-wide transition-colors">Update password</button>
                <button data-testid="edit-member-send-reset" onClick={sendResetLink}
                  className="rounded-full border border-[#A9724E] text-[#A9724E] hover:bg-[#A9724E] hover:text-[#F4EFE6] px-5 py-2 text-xs tracking-wide transition-colors">Send reset link instead</button>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={() => setEditMem(null)} className="text-sm text-[#8a7a6a]">Cancel</button>
              <button data-testid="edit-member-save" onClick={saveEditMem} disabled={savingEdit}
                className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm transition-colors disabled:opacity-50">{savingEdit ? "Saving…" : "Save changes"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
